import styled, { css } from 'styled-components'

import { theme } from 'styles/theme'

import { thinStroke } from '../icon-stroke'
import { revealZoom } from '../reveal-zoom'

const { colors, fontSizes, lineHeights, media, radii, space } = theme

const simulationButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${space['spacing-mega']};
  padding: 0 ${space['spacing-sm']};
  border-radius: ${radii['corner-radius-pill']};
  background-color: ${colors['blue-background-strong-default']};
  transition: background-color 180ms ease;

  &:hover {
    background-color: ${colors['blue-background-strong-hover']};
  }

  &:focus-visible {
    outline: 2px solid ${colors['neutral-border-default']};
    outline-offset: 3px;
  }
`

export const Section = styled.section`
  box-sizing: border-box;
  width: 100%;
  background-color: ${colors['cashme-dark-blue']};
`

export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};
  width: 100%;
  padding-top: ${space['spacing-xxxl']};

  @media (${media.desktop}) {
    display: grid;
    grid-template-columns: 542px minmax(0, 1fr);
    gap: ${space['spacing-giga']};
    padding: 0 0 ${space['spacing-giga']} max(${space['spacing-md']}, calc((100vw - 1094px) / 2));
  }
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};
  padding: 0 ${space['spacing-md']};

  > .ds-button {
    ${simulationButton}
  }

  @media (${media.desktop}) {
    padding: ${space['spacing-giga']} 0 0;

    > .ds-button {
      width: 230px;
      gap: ${space['spacing-sm']};
    }
  }

`

export const Introduction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  h2 {
    margin: 0;
    font-size: ${fontSizes['font-size-mobile-heading-2']};
    line-height: 1.2;
    letter-spacing: -0.72px;
  }

  p {
    margin: 0;
    font-size: ${fontSizes['font-size-body-small']};
    line-height: ${lineHeights['font-line-height-lg']};
  }

  @media (${media.desktop}) {
    gap: ${space['spacing-xl']};

    h2 {
      font-size: ${fontSizes['font-size-heading-2']};
      line-height: ${lineHeights['font-line-height-sm']};
      letter-spacing: -0.4px;
    }
  }
`

export const Highlight = styled.span`
  color: ${colors['pink-text-contrast']};
  font-weight: 600;

  @media (${media.desktop}) {
    color: ${colors['cashme-blue-neon']};
  }
`

export const UseCaseList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-md']};
  margin: 0;
  padding: 0;
  list-style: none;
`

export const UseCase = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${space['spacing-md']};
  padding-bottom: ${space['spacing-md']};
  border-bottom: 1px solid rgb(255 255 255 / 12%);

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`

export const Icon = styled.span`
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  ${thinStroke}

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`

export const UseCaseCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${space['spacing-xxs']};
  min-width: 0;

  h3,
  p {
    margin: 0;
    font-size: ${fontSizes['font-size-body-small']};
    line-height: ${lineHeights['font-line-height-lg']};
  }
`

export const MediaPanel = styled.aside`
  width: 100%;
  overflow: hidden;
  border-radius: 0 80px 0 0;
  background-color: ${colors['neutral-background-default']};

  @media (${media.desktop}) {
    align-self: start;
    overflow: visible;
    border-radius: 0 0 0 80px;
  }
`

export const ImageWrapper = styled.div`
  display: none;

  @media (${media.desktop}) {
    position: relative;
    display: block;
    width: 100%;
    height: 619px;
    overflow: hidden;
    border-radius: 0 0 0 80px;

    img {
      object-fit: cover;
      object-position: right center;
      transform-origin: right center;
    }

    ${revealZoom}
  }
`

export const Callout = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-md']};
  width: 100%;
  padding: ${space['spacing-xxxl']} ${space['spacing-md']};
  background-color: ${colors['neutral-background-default']};

  h3,
  p {
    margin: 0;
    font-size: ${fontSizes['font-size-body-small']};
    line-height: ${lineHeights['font-line-height-lg']};
  }

  h3 {
    font-size: 24px;
    line-height: ${lineHeights['font-line-height-sm']};
    letter-spacing: -0.48px;
  }

  @media (${media.desktop}) {
    padding: ${space['spacing-xl']};
    border-radius: 0 0 0 80px;

    h3 {
      font-size: 20px;
      letter-spacing: -0.4px;
    }
  }
`

export const CalloutPolygon = styled.span`
  display: block;
  width: 33px;
  height: 40px;

  @media (${media.desktop}) {
    position: absolute;
    top: 31px;
    left: -16px;
  }
`

export const MobileCalloutButton = styled.div`
  width: 100%;
  padding: 0 ${space['spacing-md']};

  .ds-button {
    ${simulationButton}
  }

  @media (${media.desktop}) {
    display: none;
  }

`
